import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Instagram, Facebook, Heart } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

const Footer = () => {
  return (
    <footer className="bg-[#1a1a1a] text-white border-t border-gray-800">
      <div className="container mx-auto px-4 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-8 h-8 object-contain" />
              <span className="text-2xl font-bold">PopCrown</span>
            </div>
            <p className="text-gray-300 mb-4 text-sm leading-relaxed">
              An Unofficial Pop Icon Fan Community. Not affiliated with, endorsed by, or connected to any artist estates or rights holders.
            </p>
            <div className="flex gap-3">
              <a
                href="https://instagram.com/popcrownsocial"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com/PopCrownSocial"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://tiktok.com/@popcrownsocial"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="TikTok"
              >
                <FaTiktok className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-[#E10600] font-bold mb-4 text-lg">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-[#E10600] transition-colors underline decoration-gray-500 hover:decoration-[#E10600]">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/app" className="text-gray-300 hover:text-[#E10600] transition-colors underline decoration-gray-500 hover:decoration-[#E10600]">
                  Join Us
                </Link>
              </li>
              <li>
                <Link to="/explore" className="text-gray-300 hover:text-[#E10600] transition-colors underline decoration-gray-500 hover:decoration-[#E10600]">
                  Explore
                </Link>
              </li>
              <li>
                <Link to="/events" className="text-gray-300 hover:text-[#E10600] transition-colors underline decoration-gray-500 hover:decoration-[#E10600]">
                  Events
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-300 hover:text-[#E10600] transition-colors underline decoration-gray-500 hover:decoration-[#E10600]">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-300 hover:text-[#E10600] transition-colors underline decoration-gray-500 hover:decoration-[#E10600]">
                  FAQ
                </Link>
              </li>
              <li>
                <a 
                  href="https://members.popcrownofficial.com/auth/sign-in" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-[#E10600] transition-colors underline decoration-gray-500 hover:decoration-[#E10600]"
                >
                  Sign In
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-[#E10600] font-bold mb-4 text-lg">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className="text-gray-300 hover:text-[#E10600] transition-colors underline decoration-gray-500 hover:decoration-[#E10600]">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-300 hover:text-[#E10600] transition-colors underline decoration-gray-500 hover:decoration-[#E10600]">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-gray-300 hover:text-[#E10600] transition-colors underline decoration-gray-500 hover:decoration-[#E10600]">
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-[#E10600] font-bold mb-4 text-lg">Contact</h3>
            <a
              href="mailto:support@popcrownofficial.com"
              className="text-gray-300 hover:text-[#E10600] transition-colors break-words underline decoration-gray-500 hover:decoration-[#E10600]"
            >
              support@popcrownofficial.com
            </a>
          </div>
        </div>

        {/* Legal Disclaimers */}
        <div className="border-t border-gray-800 pt-8 space-y-4">
          <div className="text-gray-300 text-sm max-w-4xl">
            <p className="mb-3">
              <strong className="text-[#E10600]">™ Trademark & Copyright Notice:</strong> PopCrown Official Ltd and PopCrown Social are independent fan communities. We are not affiliated with, endorsed by, authorised by, or connected to any artists, brands, record labels, publishers, management companies, or any artist estates or rights holders.
            </p>
            <p className="mb-3">
              All trademarks, artist names, logos, and related imagery are the property of their respective owners. Any references are used for identification and commentary purposes only. Unless stated otherwise, PopCrown does not claim ownership of any third party rights.
            </p>
            <p className="mb-3">
              We do not host or distribute copyrighted music, videos, or photographs. Where third party media is referenced, we link to content hosted on publicly available platforms and, where possible, official or licensed sources.
            </p>
            <p>
              <strong className="text-[#E10600]">Intellectual Property and Takedown Requests:</strong> We respect intellectual property rights and will respond to valid rights holder requests. If you believe any content on this site infringes your copyright, trademark, or other rights, please contact us at <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a> with sufficient details to identify the material and your rights in it. We will review and, where appropriate, remove or disable access to the content.
            </p>
          </div>

          {/* Copyright */}
          <div className="text-center text-gray-300 text-sm pt-4">
            <p>© {new Date().getFullYear()} PopCrown Official Ltd. All rights reserved.</p>
            <p className="mt-1 inline-flex items-center gap-1.5">
              Made with <Heart className="w-4 h-4 fill-[#E10600] text-[#E10600]" aria-label="love" /> for pop culture fans worldwide
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;